import { StrictMode, type ReactElement, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation } from "react-router-dom";
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { getStoredSession, AuthProvider } from "./hooks/useAuth";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { InstallPromptBanner } from "./components/InstallPromptBanner";
import Dashboard from "./pages/Dashboard";
import ConceptList from "./pages/ConceptList";
import ConceptPage from "./pages/ConceptPage";
import Analytics from "./pages/Analytics";
import Welcome from "./pages/Welcome";
import StudentLogin from "./pages/StudentLogin";
import PracticeTestHome from "./pages/PracticeTestHome";
import PracticeTestStart from "./pages/PracticeTestStart";
import PracticeTestTaking from "./pages/PracticeTestTaking";
import PracticeTestResults from "./pages/PracticeTestResults";
import PracticeArchive from "./pages/PracticeArchive";
import UnderstandSessionPage from "./pages/UnderstandSessionPage";
import GuidedSessionHistory from "./pages/GuidedSessionHistory";
import GuidedSessionDetail from "./pages/GuidedSessionDetail";
import TestPrepSessionPage from "./pages/TestPrepSessionPage";
import ExamPrepOrchestrationPage from "./pages/ExamPrepOrchestrationPage";
import AppliedPracticeHistory from "./pages/AppliedPracticeHistory";
import AppliedPracticeHistoryDetail from "./pages/AppliedPracticeHistoryDetail";
import CheckInCompletionPage from "./pages/CheckInCompletionPage";
import DrillPage from "./pages/DrillPage";
import DrillArchive from "./pages/DrillArchive";
import Badges from "./pages/Badges";
import Assignments from "./pages/Assignments";
import AssignmentTaking from "./pages/AssignmentTaking";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Disclaimer from "./pages/Disclaimer";
import './index.css';

// Root redirect component
function RootRedirect(): ReactElement {
  const session = getStoredSession();
  return session ? <Navigate to="/dashboard" replace /> : <Navigate to="/welcome" replace />;
}

// Scroll to top on route change
function ScrollToTop(): null {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Layout component with Navbar for authenticated pages
function ProtectedLayout(): ReactElement {
  return (
    <div className="min-h-screen bg-mist flex flex-col overflow-x-hidden">
      <ScrollToTop />
      <Navbar />
      <InstallPromptBanner />
      {/* Add padding-top for floating navbar */}
      <div className="pt-24">
        <Outlet />
      </div>
      <Footer />
    </div>
  );
}

const router = createBrowserRouter([
  { path: "/", element: <RootRedirect /> },
  { path: "/welcome", element: <Welcome /> },
  { path: "/login", element: <StudentLogin /> },
  { path: "/privacy", element: <PrivacyPolicy /> },
  { path: "/terms", element: <TermsOfService /> },
  { path: "/disclaimer", element: <Disclaimer /> },
  {
    element: <ProtectedLayout />,
    children: [
      { path: "/dashboard", element: <Dashboard /> },
      { path: "/understand-something", element: <UnderstandSessionPage /> },
      { path: "/understand-something/history", element: <GuidedSessionHistory /> },
      { path: "/understand-something/history/:sessionId", element: <GuidedSessionDetail /> },
      { path: "/prepare-for-test", element: <TestPrepSessionPage /> },
      { path: "/exam-prep-plan", element: <ExamPrepOrchestrationPage /> },
      { path: "/practice-history", element: <AppliedPracticeHistory /> },
      { path: "/practice-history/:conceptId/:difficulty", element: <AppliedPracticeHistoryDetail /> },
      { path: "/check-in/:checkinId", element: <CheckInCompletionPage /> },
      { path: "/concepts", element: <ConceptList /> },
      { path: "/concept/:id", element: <ConceptPage /> },
      { path: "/test-history", element: <Navigate to="/practice-test/archive" replace /> },
      { path: "/analytics", element: <Analytics /> },
      { path: "/practice-test", element: <PracticeTestHome /> },
      { path: "/practice-test/archive", element: <PracticeArchive /> },
      { path: "/practice-test/:examType", element: <PracticeTestStart /> },
      { path: "/practice-test/attempt/:setId", element: <PracticeTestTaking /> },
      { path: "/practice-test/results/:attemptId", element: <PracticeTestResults /> },
      { path: "/drill", element: <DrillPage /> },
      { path: "/drill-archive", element: <DrillArchive /> },
      { path: "/badges", element: <Badges /> },
      { path: "/assignments", element: <Assignments /> },
      { path: "/assignments/:assignmentId/take", element: <AssignmentTaking /> },
    ]
  }
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>
);
